'use client';

import { c as _c } from "react/compiler-runtime";
import { useRouter } from 'next/navigation';
import { useCallback, useEffect } from 'react';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getPortfolioUrl } from '@/helpers/routing';

/**
 * Module-level so the deferred navigation survives the unmount of whichever nav
 * surface was clicked. The burger menu, for instance, unmounts the moment an
 * item is selected, but the always-mounted main nav still resolves the intent
 * once the wallet connects.
 */
let isAwaitingPortfolioConnect = false;

/**
 * Gates the Portfolio nav entry behind wallet connect.
 *
 * Disconnected, the Portfolio option carries no href (see `getNavigationOptions`),
 * so clicking it calls the returned opener instead of routing to the bare
 * `/portfolio` connect screen, an empty state we'd rather not show. Once the user
 * connects we carry them into their address-scoped Portfolio, preserving the
 * prior post-connect redirect. Dismissing the modal without connecting drops the
 * intent, so a later, unrelated connection doesn't yank the user to Portfolio.
 *
 * @returns A callback that opens the connect modal and arms the deferred nav.
 */
export const usePortfolioConnectGate = () => {
  const $ = _c(9);
  const {
    account,
    modal
  } = useWeb3Context();
  const router = useRouter();
  const userAddress = account.isConnected ? account.address : undefined;
  let t0;
  let t1;
  if ($[0] !== router || $[1] !== userAddress) {
    t0 = () => {
      if (isAwaitingPortfolioConnect && userAddress) {
        isAwaitingPortfolioConnect = false;
        router.push(getPortfolioUrl({
          userAddress
        }));
      }
    };
    t1 = [userAddress, router];
    $[0] = router;
    $[1] = userAddress;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[4] !== modal) {
    t2 = () => modal.subscribeState(state => {
      if (!state.open && !modal.getIsConnectedState()) {
        isAwaitingPortfolioConnect = false;
      }
    });
    t3 = [modal];
    $[4] = modal;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] !== modal) {
    t4 = () => {
      isAwaitingPortfolioConnect = true;
      modal.open();
    };
    $[7] = modal;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};